import React from 'react';
const { useState, useEffect } = React;
import { coachCall } from './shared.jsx';
import { adminCall, mutedSt, cardSt } from '../diagnostic/shared.jsx';
import { ROLES, t } from '../../diagnosticConfig.js';

// Possible duplicate cards (same email or same name): an explicit review, never
// an automatic merge. The coach either marks them as different people or moves
// each participation, one by one and with confirmation, to the card that stays
// (coaching: coaching-admin › moveMembership; Diagnostic: diagnostic-admin ›
// linkParticipantPerson, the same link as in Diagnostic → Participantes). The
// name and email kept by each diagnostic do not change. An emptied card can then
// be deleted with the usual check (no participations left).

const REASONS = { same_email: 'mismo email', same_name: 'mismo nombre' };

function Side({ p }) {
  return (
    <div style={{ flex: '1 1 200px', minWidth: 0, border: '1px solid var(--globant-border)', borderRadius: 12, padding: 10, background: '#FFFFFF' }}>
      <b>{p.name}</b>
      <div style={mutedSt}>{p.email || 'sin email'}</div>
      <div style={{ ...mutedSt, marginTop: 4 }}>{p.coaching} {p.coaching === 1 ? 'proceso' : 'procesos'} de acompañamiento · {p.diagnostics} {p.diagnostics === 1 ? 'diagnóstico' : 'diagnósticos'}</div>
    </div>
  );
}

/** Moving the participations of one card (from) to the card that stays (to). */
function Unify({ from, to, onDone, onCancel }) {
  const [items, setItems] = useState(null);
  const [error, setError] = useState('');
  const load = async () => {
    try {
      const [v, d] = await Promise.all([coachCall({ action: 'personView', personId: from.id }), adminCall({ action: 'personDiagnostics', personId: from.id })]);
      setItems([
        ...v.participations.filter(x => x.memberStatus !== 'left').map(x => ({ kind: 'coaching', id: x.memberId, label: `Acompañamiento · ${x.process.name}` })),
        ...d.diagnostics.map(x => ({ kind: 'diagnostic', id: x.participantId, label: `Diagnostic · ${x.diagnosticName}${x.role ? ` (${t(ROLES[x.role]?.label) || x.role})` : ''}` })),
      ]);
    } catch (e) { setError(e.message); }
  };
  useEffect(() => { load(); }, [from.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const move = async (item) => {
    if (!window.confirm(`¿Mover "${item.label}" de la ficha de ${from.name} a la de ${to.name}?\n\nEl contenido, el estado y los links de esa participación no cambian.`)) return;
    setError('');
    try {
      if (item.kind === 'coaching') await coachCall({ action: 'moveMembership', memberId: item.id, toPersonId: to.id, confirm: true });
      else await adminCall({ action: 'linkParticipantPerson', participantId: item.id, personId: to.id });
      await load();
    } catch (e) { setError(e.message); }
  };
  const remove = async () => {
    if (!window.confirm(`¿Borrar la ficha de ${from.name}? Ya no tiene participaciones. El contacto comercial vinculado, si hay, no cambia.`)) return;
    try { await coachCall({ action: 'deletePerson', id: from.id, confirm: true }); onDone(); } catch (e) { setError(e.message); }
  };
  return (
    <div style={{ display: 'grid', gap: 8, marginTop: 8 }}>
      <div style={{ fontSize: 13 }}>Unificar en <b>{to.name}</b>: mueve cada participación de <b>{from.name}</b>, una por una.</div>
      {error && <div style={{ ...mutedSt, color: '#B4362C' }}>{error}</div>}
      {!items ? <div style={mutedSt}>Cargando…</div> : !items.length ? (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={mutedSt}>La ficha de {from.name} ya no tiene participaciones.</span>
          <button type="button" className="action-btn btn-danger" onClick={remove}>Borrar esa ficha</button>
        </div>
      ) : items.map(it => (
        <div key={it.id} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', fontSize: 13 }}>
          <span style={{ flex: '1 1 220px' }}>{it.label}</span>
          <button type="button" className="action-btn" onClick={() => move(it)}>Mover a {to.name}</button>
        </div>
      ))}
      <div><button type="button" className="action-btn" onClick={onCancel}>Listo</button></div>
    </div>
  );
}

export default function DuplicateReview({ onChanged }) {
  const [pairs, setPairs] = useState([]);
  const [unify, setUnify] = useState(null);
  const [error, setError] = useState('');
  const load = () => coachCall({ action: 'duplicates' }).then(d => setPairs(d.pairs)).catch(() => setPairs([]));
  useEffect(() => { load(); }, []);
  if (!pairs.length) return null;
  const different = async (pair) => {
    if (!window.confirm(`¿${pair.a.name} y ${pair.b.name} son personas distintas? El par deja de sugerirse.`)) return;
    try { setPairs((await coachCall({ action: 'notDuplicate', personId: pair.a.id, otherId: pair.b.id })).pairs); } catch (e) { setError(e.message); }
  };
  return (
    <details style={{ ...cardSt, background: '#FBF8F1', marginBottom: 12 }}>
      <summary style={{ cursor: 'pointer', fontWeight: 700 }}>Posibles duplicados ({pairs.length}) · revisar</summary>
      <p style={{ ...mutedSt, margin: '6px 0 10px' }}>Fichas con el mismo email o el mismo nombre. Nada se une solo: decide en cada caso.</p>
      {error && <div style={{ ...mutedSt, color: '#B4362C' }}>{error}</div>}
      <div style={{ display: 'grid', gap: 12 }}>
        {pairs.map(pair => {
          const key = `${pair.a.id}-${pair.b.id}`;
          return (
            <div key={key} style={{ borderTop: '1px solid var(--globant-border)', paddingTop: 10 }}>
              <div style={{ ...mutedSt, marginBottom: 6 }}>Coinciden en: {pair.reasons.map(r => REASONS[r] || r).join(' y ')}</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}><Side p={pair.a} /><Side p={pair.b} /></div>
              {unify?.key === key ? (
                <Unify from={unify.from} to={unify.to} onCancel={() => { setUnify(null); load(); onChanged?.(); }} onDone={() => { setUnify(null); load(); onChanged?.(); }} />
              ) : (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                  <button type="button" className="action-btn" onClick={() => different(pair)}>Son personas distintas</button>
                  <button type="button" className="action-btn" onClick={() => setUnify({ key, from: pair.b, to: pair.a })}>Unificar en {pair.a.name} ({pair.a.email || 'sin email'})</button>
                  <button type="button" className="action-btn" onClick={() => setUnify({ key, from: pair.a, to: pair.b })}>Unificar en {pair.b.name} ({pair.b.email || 'sin email'})</button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </details>
  );
}
